import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateService } from '../../../shared/services/date.service';
import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../shared/services/auth.service';
import { UtilService } from '../../../shared/services/util.service';

@Component({
  standalone: false,
  selector: 'app-view-dispute',
  templateUrl: './view-dispute.component.html',
  styleUrl: './view-dispute.component.scss'
})
export class ViewDisputeComponent {

  @Input() disputeData: any;
  @Output() onSave = new EventEmitter<boolean>();
  user: any;
  statusOptions = [];
  newComment: string = '';
  originalStatus: any;
  selectedStatus: string | null = null;
  isUpdating: boolean = false;
  updateError: string = '';
  /**
   * Idempotency key for the in-flight status update. Approving a Refund dispute moves money,
   * so a retry must carry the SAME key to be deduplicated; it is only cleared once the server
   * has answered definitively.
   */
  private updateIdempotencyKey: string | null = null;


  get transactionAmount() {
    try {
      const conversions = JSON.parse(this.disputeData?.transactionId?.conversions);
      return conversions.customerPays.currency+' '+conversions.customerPays.amount;
    } catch (e) {
      return '0';
    }
  }

  get isAccountsUser(): boolean {
    return this.user?.department === 'Accounts' && (this.user?.role === 'Executive' || this.user?.role === 'Manager');
  }

  constructor(private activeModal: NgbActiveModal,
    private _date: DateService,
    private _ds: ApiService,
    private _auth: AuthService,
    private _util: UtilService
  ) {

  }

  ngOnInit() {
    const vm = this;
    vm.user = vm._auth.getUser();
    vm.originalStatus = vm.disputeData?.status;
    if (vm.user.department === 'Accounts' && vm.user.role === 'Executive') {
      vm.statusOptions = [
        { value: 'InProgress', label: 'In-Progress' },
        { value: 'Resolved', label: 'Resolved' },
      ];
    } else if (vm.user.department === 'Accounts' && vm.user.role === 'Manager') {
      vm.statusOptions = [
        { value: 'Resolved', label: 'Resolved' },
        { value: 'Rejected', label: 'Rejected' },
        { value: 'Refunded', label: 'Refunded' }
      ];
    } else {
      // Default status options for other roles
      vm.statusOptions = [
        { value: 'Open', label: 'Open' },
        { value: 'InProgress', label: 'In-Progress' },
        { value: 'Resolved', label: 'Resolved' },
        { value: 'Rejected', label: 'Rejected' },
        { value: 'Refunded', label: 'Refunded' }
      ];
    }
  }

  onClose() {
    const vm = this;
    vm.activeModal.close(true);
  }

  dateFormat(value: any) {
    const vm = this;
    return vm._date.getFormattedDateWithAMPM(value);
  }

  onUpdate() {
    const vm = this;
    if (!vm.disputeData || !vm.newComment || !vm.selectedStatus) return;
    // Guard against a double-click racing two claims for the same key (the second would come
    // back as a 409 "already in progress").
    if (vm.isUpdating) return;

    vm.isUpdating = true;
    vm.updateError = '';

    const commentObj = {
      comment: vm.newComment,
      user: vm.user?._id,
      date: Date.now()
    };

    // Build the payload without mutating `disputeData`. Appending to the live array before
    // the request meant a failed attempt left a phantom comment behind and a retry sent it
    // twice.
    const reqObj = {};
    reqObj['_id'] = vm.disputeData._id;
    reqObj['userComments'] = [...(vm.disputeData.userComments || []), commentObj];
    reqObj['status'] = vm.selectedStatus;

    // `/admin/update-disputes` is guarded by `requireIdempotencyKeyAtomic` and is rejected
    // without this header. The same key is reused on retry so approving a refund twice cannot
    // pay out twice.
    if (!vm.updateIdempotencyKey) {
      vm.updateIdempotencyKey = vm._util.newIdempotencyKey();
    }

    const apiUrl = '/admin/update-disputes';
    vm._ds.postData(apiUrl, reqObj, {
      headers: { 'idempotency-key': vm.updateIdempotencyKey }
    }).subscribe({
      next: (res: any) => {
        vm.isUpdating = false;
        // Definitive answer received — the key is spent; a further edit is a new operation.
        vm.updateIdempotencyKey = null;
        if (res && !res.error) {
          vm.disputeData.status = vm.selectedStatus;
          vm.disputeData.userComments = reqObj['userComments'];
          vm.activeModal.close(true);
          vm.onSave.emit();
        } else {
          vm.updateError = res?.message || 'Failed to update dispute';
        }
      },
      error: (err: any) => {
        vm.isUpdating = false;
        // Keep the key: the outcome is unknown, so a resubmit must be deduplicated rather
        // than risk a second refund payout.
        vm.updateError =
          (typeof err === 'string' ? err : null) ||
          err?.error?.message ||
          err?.error?.errors?.[0]?.msg ||
          err?.message ||
          'Failed to update dispute';
      }
    });
  }

}
